import classNames from 'classnames';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { RBACGroup, RBACUser, RoleMember } from '../../../types';
import {
  graphApi,
  useAddGroupMemberMutation,
  useAddGroupOwnerMutation,
  useRemoveGroupMemberMutation,
  useRemoveGroupOwnerMutation,
} from '../../graph/services/graphApi';
import { errorMessage, withTransientRetry } from '../../graph/transientRetry';
import {
  type BucketName,
  selectAclAdds,
  selectAclDeletes,
  selectMemberAdds,
  selectMemberDeletes,
  selectOwnerAdds,
  selectOwnerDeletes,
  unstageAdd,
  unstageDelete,
} from '../services/roleManagementSlice';

type Props = {
  role?: RBACGroup;
};

type Listed = { id: string; displayName?: string };

// One staged-change group in the confirm modal. Hoisted to module scope (rather than
// defined inside the component) so it keeps a stable type identity across renders and
// React reconciles it instead of remounting the subtree each time.
function Section({
  title,
  items,
  tone,
}: {
  title: string;
  items: Listed[];
  tone: 'add' | 'remove';
}) {
  if (!items.length) return null;
  return (
    <div className="py-2">
      <div className="pb-1">{title}</div>
      <div className="border border-base-200 rounded-lg overflow-hidden">
        <ul className="divide-y divide-base-200">
          {items.map((item) => (
            <li
              key={item.id}
              className={classNames('p-2', {
                'text-error-content bg-error': tone === 'remove',
                'text-success-content bg-success': tone === 'add',
              })}
            >
              {item.displayName}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Confirm + commit all staged changes across the three relationships in one batch.
// Each write is independent: on success it patches its list cache optimistically and
// un-stages; a failure (after retries) toasts and leaves that item staged. See ADR-0002.
export default function SubmitRoleChanges({ role }: Props) {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const memberAdds = useAppSelector(selectMemberAdds);
  const memberDeletes = useAppSelector(selectMemberDeletes);
  const ownerAdds = useAppSelector(selectOwnerAdds);
  const ownerDeletes = useAppSelector(selectOwnerDeletes);
  const aclAdds = useAppSelector(selectAclAdds);
  const aclDeletes = useAppSelector(selectAclDeletes);

  const [addGroupMember] = useAddGroupMemberMutation();
  const [removeGroupMember] = useRemoveGroupMemberMutation();
  const [addGroupOwner] = useAddGroupOwnerMutation();
  const [removeGroupOwner] = useRemoveGroupOwnerMutation();

  const totalPending =
    memberAdds.length +
    memberDeletes.length +
    ownerAdds.length +
    ownerDeletes.length +
    aclAdds.length +
    aclDeletes.length;

  const modalClassName = classNames('modal fixed inset-0 flex items-center justify-center', {
    'modal-open': showModal,
  });
  const buttonClassName = classNames('btn btn-primary', { 'btn-disabled': totalPending === 0 });

  // Fire one write; on success un-stage it. Transient Graph failures (concurrent-
  // modification 404 / throttling) are retried with a short backoff by withTransientRetry;
  // anything else — or a transient error still failing after the retries — toasts and
  // stays staged.
  const run = async (op: () => Promise<unknown>, onSuccess: () => void, label: string) => {
    try {
      await withTransientRetry(op);
      onSuccess();
    } catch (error) {
      toast(`Error ${label}: ${errorMessage(error) ?? 'unknown error'}`, { type: 'error' });
    }
  };

  const handleSubmit = () => {
    if (!role || isSubmitting) return;
    setIsSubmitting(true);
    const roleOp = { groupDisplayName: role.displayName!, groupId: role.id };

    // One relationship's staged changes → independent write tasks. The mechanical part
    // (map → run → un-stage on success) lives here; each caller supplies only what
    // differs: the bucket, the two mutations, and the toast labels.
    const commit = <T extends RBACUser | RBACGroup | RoleMember>(r: {
      bucket: BucketName;
      adds: T[];
      deletes: T[];
      add: (item: T) => Promise<unknown>;
      remove: (item: T) => Promise<unknown>;
      addLabel: (item: T) => string;
      removeLabel: (item: T) => string;
      // Patch the committed item into / out of the panel's query cache on success.
      // Graph's memberOf index lags the write, so we update the cache here rather than
      // refetch (which would read stale); the Refresh button re-syncs on demand. ADR-0002.
      patchAdd: (item: T) => void;
      patchRemove: (item: T) => void;
    }) => [
      ...r.adds.map((item) =>
        run(
          () => r.add(item),
          () => {
            r.patchAdd(item);
            dispatch(unstageAdd({ bucket: r.bucket, item }));
          },
          r.addLabel(item)
        )
      ),
      ...r.deletes.map((item) =>
        run(
          () => r.remove(item),
          () => {
            r.patchRemove(item);
            dispatch(unstageDelete({ bucket: r.bucket, item }));
          },
          r.removeLabel(item)
        )
      ),
    ];

    const tasks: Promise<void>[] = [
      ...commit({
        add: (u) => addGroupMember({ ...roleOp, memberId: u.id }).unwrap(),
        addLabel: (u) => `adding ${u.displayName} to ${role.displayName}`,
        adds: memberAdds,
        bucket: 'members',
        deletes: memberDeletes,
        patchAdd: (u) =>
          dispatch(
            graphApi.util.updateQueryData('getGroupMembers', role.id, (draft) => {
              if (!draft.some((m) => m.id === u.id)) {
                draft.push(u);
                draft.sort((a, b) => a.displayName.localeCompare(b.displayName));
              }
            })
          ),
        patchRemove: (u) =>
          dispatch(
            graphApi.util.updateQueryData('getGroupMembers', role.id, (draft) =>
              draft.filter((m) => m.id !== u.id)
            )
          ),
        remove: (u) => removeGroupMember({ ...roleOp, memberId: u.id }).unwrap(),
        removeLabel: (u) => `removing ${u.displayName} from ${role.displayName}`,
      }),
      ...commit({
        add: (u) => addGroupOwner({ ...roleOp, memberId: u.id }).unwrap(),
        addLabel: (u) => `adding owner ${u.displayName} to ${role.displayName}`,
        adds: ownerAdds,
        bucket: 'owners',
        deletes: ownerDeletes,
        patchAdd: (u) =>
          dispatch(
            graphApi.util.updateQueryData('getGroupOwners', role.id, (draft) => {
              if (!draft.some((o) => o.id === u.id)) {
                draft.push(u);
                draft.sort((a, b) => a.displayName.localeCompare(b.displayName));
              }
            })
          ),
        patchRemove: (u) =>
          dispatch(
            graphApi.util.updateQueryData('getGroupOwners', role.id, (draft) =>
              draft.filter((o) => o.id !== u.id)
            )
          ),
        remove: (u) => removeGroupOwner({ ...roleOp, memberId: u.id }).unwrap(),
        removeLabel: (u) => `removing owner ${u.displayName} from ${role.displayName}`,
      }),
      // Nesting = the role becomes a member of the ACL group: groupId = ACL, member = role.
      ...commit({
        add: (a) =>
          addGroupMember({
            groupDisplayName: a.displayName!,
            groupId: a.id,
            memberId: role.id,
          }).unwrap(),
        addLabel: (a) => `nesting ${role.displayName} into ${a.displayName}`,
        adds: aclAdds,
        bucket: 'aclNesting',
        deletes: aclDeletes,
        // A newly-nested ACL is a direct membership, so transitive: false. A later
        // Refresh reconciles the rare case where the same ACL is also reached
        // transitively (the server read would re-flag it).
        patchAdd: (a) =>
          dispatch(
            graphApi.util.updateQueryData('getRoleACLGroupMembership', role.id, (draft) => {
              if (!draft.some((g) => g.id === a.id)) {
                draft.push({ displayName: a.displayName!, id: a.id, transitive: false });
                draft.sort((x, y) => x.displayName!.localeCompare(y.displayName!));
              }
            })
          ),
        patchRemove: (a) =>
          dispatch(
            graphApi.util.updateQueryData('getRoleACLGroupMembership', role.id, (draft) =>
              draft.filter((g) => g.id !== a.id)
            )
          ),
        remove: (a) =>
          removeGroupMember({
            groupDisplayName: a.displayName!,
            groupId: a.id,
            memberId: role.id,
          }).unwrap(),
        removeLabel: (a) => `un-nesting ${role.displayName} from ${a.displayName}`,
      }),
    ];

    Promise.all(tasks).then(() => {
      setIsSubmitting(false);
      setShowModal(false);
    });
  };

  if (!role) {
    return <div></div>;
  }

  return (
    <>
      <button className={buttonClassName} onClick={() => setShowModal(true)}>
        Submit
      </button>

      <div className={modalClassName}>
        <div className="bg-base-100 rounded-lg p-5 max-h-[90vh] overflow-auto">
          <div className="font-bold text-xl mb-2">Confirm changes to {role.displayName}</div>
          <div className="pb-2">
            <Section title="Members to add:" items={memberAdds} tone="add" />
            <Section title="Members to remove:" items={memberDeletes} tone="remove" />
            <Section title="ACLs to nest into:" items={aclAdds} tone="add" />
            <Section title="ACLs to un-nest from:" items={aclDeletes} tone="remove" />
            <Section title="Owners to add:" items={ownerAdds} tone="add" />
            <Section title="Owners to remove:" items={ownerDeletes} tone="remove" />
          </div>
          <div className="text-base-content mb-4">
            Are you sure you want to submit all pending changes?
          </div>
          <div className="flex justify-end">
            <button
              className="btn btn-ghost mr-2"
              onClick={() => setShowModal(false)}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className={classNames('btn btn-primary', { 'btn-disabled': isSubmitting })}
              onClick={handleSubmit}
            >
              Submit changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
